<script setup lang="ts">
type PerkIcon =
  | "iceberg"
  | "trampoline"
  | "shade"
  | "walk"
  | "shower"
  | "entry"
  | "bar";

interface Perk {
  icon: PerkIcon;
  label: string;
  note?: string;
}

interface Tariff {
  label: string;
  covers: string;
  standard: string;
  guest: string;
  savings?: string;
  guest_is_free?: boolean;
  highlight?: boolean;
}

interface BeachBlock {
  header: string;
  intro?: string;
  note?: string;
  compare_intro?: string;
  image: string;
  image_alt?: string;
  perks?: Perk[];
  tariffs: Tariff[];
}

defineProps<{
  block: BeachBlock;
}>();

const { t } = useI18n();
const localePath = useLocalePath();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-sky-50/40 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -left-16 h-80 w-80 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-orange-200/30 blur-3xl dark:bg-orange-500/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header class="mb-6 md:mb-8">
        <div
          class="flex flex-wrap items-center gap-x-2.5 gap-y-1 font-body text-xs uppercase tracking-[0.3em] text-teal-700 dark:text-teal-300"
        >
          <span>{{ t("amenities.beach.meta_shore") }}</span>
          <span
            aria-hidden="true"
            class="text-teal-400/70 dark:text-teal-500/60"
            >·</span
          >
          <span>{{ t("amenities.beach.meta_guarded") }}</span>
          <span
            aria-hidden="true"
            class="text-teal-400/70 dark:text-teal-500/60"
            >·</span
          >
          <span>{{ t("amenities.beach.meta_season") }}</span>
        </div>
        <h2
          class="mt-3 max-w-5xl font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.compare_intro"
          class="mt-3 max-w-3xl font-body text-base leading-relaxed text-slate-600 md:mt-4 md:text-lg dark:text-slate-300"
        >
          {{ block.compare_intro }}
        </p>
      </header>

      <div class="grid items-stretch gap-4 md:grid-cols-5 md:gap-5">
        <article
          class="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl md:col-span-3 dark:border-slate-700 dark:bg-slate-800/60"
        >
          <div class="aspect-[3/2] w-full overflow-hidden">
            <NuxtImg
              :src="block.image"
              :alt="block.image_alt ?? block.header"
              loading="lazy"
              width="1500"
              height="1000"
              sizes="100vw md:60vw"
              class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
          </div>
        </article>

        <div class="flex flex-col gap-3 md:col-span-2">
          <div
            v-if="block.perks?.length"
            class="grid grid-cols-2 gap-3 md:flex-1"
          >
            <div
              v-for="perk in block.perks"
              :key="perk.icon + perk.label"
              class="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-3 py-3 shadow-sm backdrop-blur-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-800/50"
            >
            <span
              class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
            >
              <svg
                v-if="perk.icon === 'iceberg'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M3 20h18" />
                <path d="M6 20 12 6l6 14" />
                <path d="M9 15h6" />
              </svg>
              <svg
                v-else-if="perk.icon === 'trampoline'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <ellipse cx="12" cy="10" rx="9" ry="3" />
                <path d="M5 12v6M19 12v6M8 11v7M16 11v7" />
              </svg>
              <svg
                v-else-if="perk.icon === 'shade'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3c-5 4-7 8-7 11h14c0-3-2-7-7-11Z" />
                <path d="M12 14v7" />
              </svg>
              <svg
                v-else-if="perk.icon === 'walk'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="13" cy="4" r="2" />
                <path d="M7 20l3-6 2-2 3 2 2 6" />
                <path d="M9 9l3-2 3 3 3 1" />
              </svg>
              <svg
                v-else-if="perk.icon === 'entry'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4Z" />
              </svg>
              <svg
                v-else-if="perk.icon === 'bar'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M4 3h16l-7 10v6h3v2H8v-2h3v-6L4 3Z" />
              </svg>
              <svg
                v-else
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 2v5M8 6c0-2 2-4 4-4" />
                <path d="M6 12h12l-1 8H7l-1-8Z" />
                <path d="M9 16v2M12 16v2M15 16v2" />
              </svg>
            </span>
            <div class="min-w-0 flex-1">
              <span
                class="block font-display text-sm italic text-slate-900 md:text-base dark:text-white"
              >
                {{ perk.label }}
              </span>
              <span
                v-if="perk.note"
                class="block font-body text-xs text-slate-500 dark:text-slate-400"
              >
                {{ perk.note }}
              </span>
            </div>
          </div>
          </div>

          <article
            v-for="tariff in block.tariffs"
            :key="tariff.label"
            class="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md md:p-6 dark:border-slate-700 dark:bg-slate-800/50"
          >
            <div class="flex flex-col gap-1">
              <span
                class="font-body text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400"
              >
                {{ tariff.covers }}
              </span>
              <h4
                class="font-display text-base italic text-slate-900 md:text-lg dark:text-white"
              >
                {{ tariff.label }}
              </h4>
            </div>

            <div
              class="mt-4 grid grid-cols-[1fr_auto_1fr] items-end gap-2 md:gap-3"
            >
              <div>
                <span
                  class="block font-body text-[9px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500"
                >
                  {{ t("amenities.compare.walkin_label") }}
                </span>
                <span
                  class="mt-1 block font-display text-xl tabular-nums text-slate-400 line-through decoration-2 decoration-slate-300 md:text-2xl dark:text-slate-500 dark:decoration-slate-600"
                >
                  {{ tariff.standard }}
                </span>
              </div>

              <span
                aria-hidden="true"
                class="mb-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
              >
                <svg
                  class="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </span>

              <div class="text-right">
                <span
                  class="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 font-body text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                >
                  {{ t("amenities.compare.guest_label") }}
                </span>
                <span
                  class="mt-1 block font-display text-2xl font-semibold tabular-nums text-slate-900 md:text-3xl dark:text-white"
                >
                  {{ tariff.guest }}
                </span>
              </div>
            </div>

            <div
              v-if="tariff.savings"
              class="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-dashed border-slate-200 pt-3 dark:border-slate-700"
            >
              <span
                class="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 font-body text-xs font-semibold text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
              >
                {{ t("amenities.compare.savings_prefix") }} {{ tariff.savings }}
              </span>
            </div>
          </article>
        </div>
      </div>

      <div
        class="mt-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5 md:mt-6"
      >
        <p
          v-if="block.note"
          class="inline-flex items-start gap-2 font-body text-sm text-slate-600 dark:text-slate-300"
        >
          <svg
            class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <span>{{ block.note }}</span>
        </p>
        <NuxtLink
          :to="localePath('/prices')"
          class="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-amber-400/60 bg-amber-50 px-4 py-2 font-body text-sm font-semibold text-amber-800 shadow-sm transition-all duration-300 hover:border-amber-500 hover:bg-amber-100 hover:shadow-md dark:border-amber-400/40 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-900/40"
        >
          {{ t('amenities.links.viewFullPrices') }}
          <svg
            class="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
